import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Zenova custom: "Cosa cerchi" behaves like a required single-choice filter.
 */
const COSA_CERCHI_PARAM = 'filter.p.m.custom.cosa_cerchi';
const COSA_CERCHI_DATA = 'filter-p-m-custom-cosa_cerchi';
const ZENOVA_NOTICE_ID = 'zenova-cosa-cerchi-notice';
const ZENOVA_STYLE_ID = 'zenova-cosa-cerchi-style';
const ZENOVA_SHELL_SELECTOR = '[data-zenova-collection-shell]';

/**
 * Fallback vuoto: nel tuo codice veniva chiamata syncZenovaMobileNotice(),
 * ma nel file che mi hai mandato non era definita.
 * Così evitiamo errori JS che bloccano tutto.
 */
function syncZenovaMobileNotice() {
  // No-op intenzionale.
}

function getCosaCerchiWrapper(root = document) {
  return root.querySelector(`[data-filter-param-name="${COSA_CERCHI_DATA}"]`);
}

function getStaticCosaCerchiWrapper(root = document) {
  const wrappers = Array.from(
    root.querySelectorAll(`[data-filter-param-name="${COSA_CERCHI_DATA}"]`)
  );

  return wrappers.find((wrapper) => {
    return !wrapper.closest('dialog, .facets-drawer, [id*="drawer"], [class*="drawer"]');
  }) || null;
}

function getCosaCerchiSummary(root = document) {
  return root.querySelector('.facets__summary--cosa-cerchi');
}

function getCosaCerchiDetails(root = document) {
  const wrapper = getCosaCerchiWrapper(root);

  if (wrapper) {
    const detailsFromWrapper = wrapper.closest('details');
    if (detailsFromWrapper instanceof HTMLDetailsElement) {
      return detailsFromWrapper;
    }
  }

  const summary = getCosaCerchiSummary(root);

  if (summary) {
    const detailsFromSummary = summary.closest('details');
    if (detailsFromSummary instanceof HTMLDetailsElement) {
      return detailsFromSummary;
    }
  }

  return null;
}

function openZenovaCosaCerchiFilter(root = document) {
  if (hasCosaCerchiSelection(root)) return;

  const details = getCosaCerchiDetails(root);
  if (!(details instanceof HTMLDetailsElement)) return;

  details.open = true;
  details.classList.add('zenova-cosa-cerchi-details-open');

  const summary = details.querySelector('summary');
  if (summary) {
    summary.setAttribute('aria-expanded', 'true');
  }
}

function enableZenovaCosaCerchiAutoClose(root = document) {
  return;
}

function getZenovaCollectionShell(root = document) {
  return root.querySelector(ZENOVA_SHELL_SELECTOR);
}

function getCosaCerchiInputs(root = document) {
  const wrapperInputs = Array.from(
    root.querySelectorAll(
      `[data-filter-param-name="${COSA_CERCHI_DATA}"] input[type="checkbox"]`
    )
  ).filter((input) => input instanceof HTMLInputElement);

  if (wrapperInputs.length) return wrapperInputs;

  return Array.from(
    root.querySelectorAll(`input[name="${COSA_CERCHI_PARAM}"]`)
  ).filter((input) => input instanceof HTMLInputElement);
}

function getCosaCerchiInputsInForm(form) {
  if (!(form instanceof HTMLFormElement)) return [];

  return Array.from(
    form.querySelectorAll(`input[name="${COSA_CERCHI_PARAM}"]`)
  ).filter((input) => input instanceof HTMLInputElement);
}

function syncSingleCosaCerchiSelectionInForm(activeInput, form) {
  if (!(activeInput instanceof HTMLInputElement)) return;
  if (!(form instanceof HTMLFormElement)) return;

  const inputs = getCosaCerchiInputsInForm(form);

  inputs.forEach((input) => {
    input.checked = false;
  });

  activeInput.checked = true;
}

function isCosaCerchiInput(input) {
  if (!(input instanceof HTMLInputElement)) return false;

  return (
    input.name === COSA_CERCHI_PARAM ||
    !!input.closest(`[data-filter-param-name="${COSA_CERCHI_DATA}"]`)
  );
}

function hasCosaCerchiSelection(root = document) {
  return getCosaCerchiInputs(root).some((input) => input.checked);
}

function ensureZenovaCosaCerchiStyle() {
  if (document.getElementById(ZENOVA_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = ZENOVA_STYLE_ID;
  style.textContent = `
    .zenova-cosa-cerchi-locked #ResultsList [data-testid="product-grid"],
    .zenova-cosa-cerchi-locked #ResultsList .pagination,
    .zenova-cosa-cerchi-locked #ResultsList .pagination-controls,
    .zenova-cosa-cerchi-locked #ResultsList [ref="viewMorePrevious"],
    .zenova-cosa-cerchi-locked #ResultsList [ref="viewMoreNext"],
    .zenova-cosa-cerchi-locked #ResultsList .main-collection-grid__empty {
      display: none !important;
    }

    .zenova-cosa-cerchi-notice {
      margin: 10px auto 0;
      text-align: center;
      font-size: 13px;
      line-height: 1.4;
      opacity: 0.58;
      pointer-events: none;
    }

    .zenova-cosa-cerchi-notice__title,
    .zenova-cosa-cerchi-notice__text {
      margin: 0;
    }

    .zenova-static-cosa-hint {
      display: none;
    }

    @media screen and (max-width: 749px) {
      .zenova-static-cosa-hint {
        display: block;
        width: 100%;
        text-align: center;
        margin-top: -6px;
        margin-bottom: 14px;
        font-size: 11px;
        line-height: 1;
        font-weight: 400;
        letter-spacing: 0.04em;
        color: currentColor;
        opacity: 0.35;
        text-transform: lowercase;
        pointer-events: none;
      }
    }
  `;

  document.head.appendChild(style);
}

function syncZenovaCosaCerchiState(root = document) {
  ensureZenovaCosaCerchiStyle();

  const locked = !hasCosaCerchiSelection(root);

  document.documentElement.classList.toggle('zenova-cosa-cerchi-locked', locked);

  if (document.body) {
    document.body.classList.toggle('zenova-cosa-cerchi-locked', locked);
  }

  let notice = document.querySelector(`#${ZENOVA_NOTICE_ID}`);

  if (locked) {
    if (!notice) {
      notice = document.createElement('div');
      notice.id = ZENOVA_NOTICE_ID;
      notice.className = 'zenova-cosa-cerchi-notice';
      notice.innerHTML = `
        <p class="zenova-cosa-cerchi-notice__text">Scegli prima cosa cerchi per visualizzare i prodotti.</p>
      `;

      const finder =
        document.querySelector('.zenova-product-finder') ||
        document.querySelector('.facets__filters-wrapper--zenova') ||
        getCosaCerchiWrapper(root);

      if (finder && finder.parentNode) {
        finder.parentNode.insertBefore(notice, finder.nextSibling);
      }
    }
  } else if (notice) {
    notice.remove();
  }
}

function syncZenovaStaticCosaCerchiHint(root = document) {
  return;
}

function initZenovaCosaCerchi(root = document) {
  syncZenovaCosaCerchiState(root);
  openZenovaCosaCerchiFilter(root);
  enableZenovaCosaCerchiAutoClose(root);
  syncZenovaStaticCosaCerchiHint(root);
}

/**
 * Handles facets form functionality
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData]
   * @returns {URLSearchParams}
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    const newParameters = new URLSearchParams(/** @type {any} */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * @returns {string}
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';

    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * Handles individual facet input functionality
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} [event]
   */
  updateFilters(event) {
    const path = typeof event?.composedPath === 'function' ? event.composedPath() : [];
    let activeInput = null;

    for (const node of path) {
      if (node instanceof HTMLInputElement && node.type === 'checkbox') {
        activeInput = node;
        break;
      }

      if (node instanceof HTMLLabelElement && node.control instanceof HTMLInputElement) {
        activeInput = node.control;
        break;
      }
    }

    if (!activeInput && event?.target instanceof HTMLInputElement) {
      activeInput = event.target;
    }

    if (isCosaCerchiInput(activeInput)) {
      const form = activeInput.form || this.closest('form');
      syncSingleCosaCerchiSelectionInForm(activeInput, form);
    }

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();

    window.requestAnimationFrame(() => {
      initZenovaCosaCerchi(document);
    });

    window.setTimeout(() => {
      initZenovaCosaCerchi(document);
      syncZenovaAltroTriggerLabel(document);
    }, 150);

    window.setTimeout(() => {
      initZenovaCosaCerchi(document);
      syncZenovaAltroTriggerLabel(document);
    }, 500);
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const closestInput =
      event.target.closest('label')?.control ||
      event.target.querySelector('input') ||
      null;

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      if (isCosaCerchiInput(closestInput)) {
        const form = closestInput.form || this.closest('form');
        syncSingleCosaCerchiSelectionInForm(closestInput, form);
      } else {
        closestInput.checked = !closestInput.checked;
      }

      this.updateFilters({ target: closestInput });
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);
    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters.entries()) {
      url.searchParams.append(key, value);
    }

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * Handles price facet functionality
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;

  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * @param {string} format
   * @returns {string}
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * @param {string} displayValue
   * @param {string} currency
   * @returns {number}
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    const value = this.#parseDisplayValue(input.value, currency);
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');

    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();

    window.requestAnimationFrame(() => {
      initZenovaCosaCerchi(document);
    });

    window.setTimeout(() => {
      initZenovaCosaCerchi(document);
      syncZenovaAltroTriggerLabel(document);
    }, 150);

    window.setTimeout(() => {
      initZenovaCosaCerchi(document);
      syncZenovaAltroTriggerLabel(document);
    }, 500);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;

    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * Handles removal of individual facet filters
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {{form?: string}} data
   * @param {Event} event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;

    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;

    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);

    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');

          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;

    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      const selectedOption = listbox.querySelector('[aria-selected="true"]');

      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * @param {Element[]} options
   * @param {number} newIndex
   */
  #moveFocus(options, newIndex) {
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    const targetOption = options[newIndex];

    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * @param {Element} option
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');

    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });

      option.setAttribute('aria-selected', 'true');
      input.click();
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;

    if (details instanceof HTMLDetailsElement) {
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;

      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * @param {Event} event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') ||
      this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const isMobile = window.innerWidth < 750;
    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');

        inputs.forEach((input) => {
          if (input instanceof HTMLInputElement) input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');

        if (selectElement instanceof HTMLSelectElement) {
          selectElement.disabled = true;
        }
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');

        inputs.forEach((input) => {
          if (input instanceof HTMLInputElement) input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');

        if (selectElement instanceof HTMLSelectElement) {
          selectElement.disabled = false;
        }
      }
    }

    const { details } = this.refs;

    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  /**
   * @param {Event} event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');

    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');

    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * Handles facet status display
 */
class FacetStatusComponent extends Component {
  /**
   * @param {HTMLInputElement[]} checkedInputElements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * @param {HTMLInputElement[]} checkedInputElements
   * @param {number} checkedInputElementsCount
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * @param {HTMLInputElement[]} checkedInputElements
   * @param {number} checkedInputElementsCount
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * @param {HTMLInputElement} minInput
   * @param {HTMLInputElement} maxInput
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);

    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * @param {string} value
   * @param {string} fallback
   * @param {string} currency
   * @returns {number}
   */
  #parseCents(value, fallback = '0', currency = '') {
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * @param {number} moneyValue
   * @returns {string}
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Zenova custom: label bottone Altro.
 */
function getZenovaSelectedCosaCerchiLabel(root = document) {
  const checkedInput = root.querySelector(
    `input[name="${COSA_CERCHI_PARAM}"]:checked`
  );

  if (!(checkedInput instanceof HTMLInputElement)) return '';

  const dataLabel = checkedInput.dataset.label;
  if (dataLabel) return dataLabel.trim();

  const label = checkedInput.closest('label');

  if (label) {
    return label.textContent.replace(/\s+/g, ' ').trim();
  }

  return checkedInput.value || '';
}

function formatZenovaFilterCategoryName(value) {
  if (!value) return '';

  return value
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function syncZenovaAltroTriggerLabel(root = document) {
  const labels = root.querySelectorAll('.zenova-altro-trigger__label');

  if (!labels.length) return;

  const selectedCategory = formatZenovaFilterCategoryName(
    getZenovaSelectedCosaCerchiLabel(root)
  );

  labels.forEach((label) => {
    label.textContent = selectedCategory ? `Filtra ${selectedCategory}` : 'Filtra';
  });
}

document.addEventListener(ThemeEvents.FilterUpdate, () => {
  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaMobileNotice();
    syncZenovaAltroTriggerLabel(document);
  }, 150);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaMobileNotice();
    syncZenovaAltroTriggerLabel(document);
  }, 500);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaMobileNotice();
    syncZenovaAltroTriggerLabel(document);
  }, 1000);
});

window.addEventListener('pageshow', () => {
  initZenovaCosaCerchi(document);
  syncZenovaMobileNotice();
  syncZenovaAltroTriggerLabel(document);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaAltroTriggerLabel(document);
  }, 300);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaAltroTriggerLabel(document);
  }, 1000);
});

document.addEventListener('DOMContentLoaded', () => {
  initZenovaCosaCerchi(document);
  syncZenovaMobileNotice();
  syncZenovaAltroTriggerLabel(document);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaAltroTriggerLabel(document);
  }, 300);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaAltroTriggerLabel(document);
  }, 1000);
});

window.addEventListener('load', () => {
  initZenovaCosaCerchi(document);
  syncZenovaAltroTriggerLabel(document);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaAltroTriggerLabel(document);
  }, 300);

  window.setTimeout(() => {
    initZenovaCosaCerchi(document);
    syncZenovaAltroTriggerLabel(document);
  }, 1000);
});